import { Auth } from '@/service/amplifyAuth.ts';
import { fetchPatientDataByRegistrationCode } from '@/service/registration/registrationService.js';

import { DEFAULT_COUNTRY } from '@/utils/constants.js';
import { normalizePhoneNumber } from '@/utils/formUtils.js';
import { processAPIWithHeaders, registerUser } from '@/utils/helpers.js';

import { useConfigStore } from '@/store.js';

// Thrown when a sign-up code is accepted but the password typed now differs from the unfinished sign-up's one.
// The code verified the email, so the patient can set a new password through the reset flow.
export const SIGN_UP_CONFIRMED_WITH_OTHER_PASSWORD = 'SignUpConfirmedWithOtherPassword';

// Helper function to get gender code from display value
const getGenderCode = (selectedGender, genderValues) => {
    const genderCode = genderValues.find(
        ({ gender_display_value }) => gender_display_value[0].text === selectedGender
    )?.gender_api_value;

    if (!genderCode) {
        throw new Error('Invalid gender selection');
    }

    return genderCode;
};

export const checkEmailExists = async (email) => {
    try {
        await Auth.signIn(email, '123');
        return false;
    } catch (error) {
        return error.code === 'NotAuthorizedException';
    }
};

export const signUpWithCognito = async (
    { email, password, firstName, lastName, selectedGender, dob, phoneNumber, language },
    genderValues
) => {
    const genderCode = getGenderCode(selectedGender, genderValues);

    await Auth.signUp({
        username: email,
        password: password,
        attributes: {
            email: email,
            'custom:firstName': firstName,
            'custom:lastName': lastName,
            'custom:gender': genderCode,
            'custom:dob': dob,
            'custom:language': language,
            'custom:phone': normalizePhoneNumber(phoneNumber),
        },
    });
};

// Profile body for POST /secure/users when the account was confirmed outside the registration flow.
export const buildUserFromCognitoAttributes = (username, attributes) => ({
    id: username,
    email: attributes.email,
    name: {
        first: attributes['custom:firstName'],
        middle: attributes['custom:middleName'],
        last: attributes['custom:lastName'],
    },
    gender: attributes['custom:gender'],
    date_of_birth: attributes['custom:dob'],
    title: attributes['custom:title'],
    phone: { number: attributes['custom:phone'] },
});

// Sends a new sign-up code. Cognito refuses for an account that is already confirmed, which is how callers tell an
// unfinished sign-up apart from a registered patient.
export const resumeSignUp = async (email) => {
    try {
        await Auth.resendSignUp(email);
        return true;
    } catch (error) {
        console.debug('error resending sign-up code', error);
        return false;
    }
};

// Loads the signed-in patient's profile. An account confirmed outside the registration flow (login, password reset)
// has none yet, so it is created from the details stored in Cognito at sign-up. If that fails the session is ended,
// because the app would otherwise treat the patient as signed in without a profile; the next sign-in retries.
export const loadOrCreateUserProfile = async (user) => {
    const token = user.signInUserSession.idToken.jwtToken;
    const profile = await processAPIWithHeaders(`secure/users/${user.username}`, 'GET', {
        headers: { Authorization: token },
    });
    if (profile?.id) return profile;

    const attributes = user.attributes ?? (await Auth.currentUserAttributes());
    const customer_id = useConfigStore.getState().currentCustomer?.customer_id?.[0].text;
    const createdProfile = await processAPIWithHeaders('secure/users', 'POST', {
        body: buildUserFromCognitoAttributes(user.username, attributes),
        headers: {
            'Content-Type': 'application/json',
            Authorization: token,
            customer_id: customer_id,
        },
    }).catch((error) => console.debug('error creating user profile', error));
    if (createdProfile?.id) return createdProfile;

    await Auth.signOut();
    throw new Error('The user profile could not be loaded or created.');
};

const signInAfterConfirmation = async (email, password) => {
    try {
        return await Auth.signIn(email, password);
    } catch (error) {
        if (error.code === 'NotAuthorizedException') {
            throw Object.assign(new Error('The password does not match the unfinished sign-up.'), {
                code: SIGN_UP_CONFIRMED_WITH_OTHER_PASSWORD,
            });
        }
        throw error;
    }
};

export const confirmCognitoSignUp = async (
    {
        email,
        verificationCode,
        password,
        firstName,
        lastName,
        selectedGender,
        dob,
        phoneNumber,
        street,
        houseNumber,
        zipCode,
        city,
        country,
        language,
        isAnalyticsConfirmed,
    },
    genderValues,
    countryList,
    healthInsuranceType
) => {
    await Auth.confirmSignUp(email, verificationCode);
    const signInUser = await signInAfterConfirmation(email, password);

    const genderCode = getGenderCode(selectedGender, genderValues);
    const effectiveCountry = country || DEFAULT_COUNTRY;

    const registerData = {
        user: {
            id: signInUser.username,
            email: email,
            name: {
                first: firstName,
                last: lastName,
            },
            gender: genderCode,
            date_of_birth: dob,
            phone: {
                number: normalizePhoneNumber(phoneNumber),
            },
        },
        account: {
            user_id: signInUser.username,
            privacy_policy_status: 'ACCEPTED',
            terms_and_conditions_status: 'ACCEPTED',
            use_of_analytics_status: isAnalyticsConfirmed ? 'ACCEPTED' : 'DECLINED',
            verification_status: 'VERIFIED',
            locale: {
                language: language,
            },
            status: 'ACTIVE',
        },
        address: {
            user_id: signInUser.username,
            street: street,
            house_number: houseNumber,
            zip: zipCode,
            city: city,
            country: {
                code: effectiveCountry,
                name: countryList[effectiveCountry.toUpperCase()],
            },
        },
        health_insurance: {
            user_id: signInUser.username,
            insurance_type: healthInsuranceType,
        },
    };

    const result = await registerUser(registerData, signInUser.signInUserSession.idToken.jwtToken);
    return { registerData, result, signInUser };
};

export const validateRegistrationCode = (hash) => {
    if (!hash || typeof hash !== 'string') {
        return false;
    }

    return hash.length >= 5; // Basic validation
};

export const getPatientDataFromRegistrationCode = async (registration_code) => {
    if (!validateRegistrationCode(registration_code)) {
        return {
            success: false,
            error: 'Invalid hash format',
        };
    }

    return fetchPatientDataByRegistrationCode(registration_code);
};
